import { useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faChartColumn,
  faChevronDown,
  faFileCirclePlus,
  faGaugeHigh,
  faGear,
  faMagnifyingGlass,
  faPlus,
} from "@fortawesome/free-solid-svg-icons";

import { useDropdown } from "./useDropdown";
import { useAccess } from "../../permissions/access";
import { canOpen } from "../../permissions/registry";

/** الإجراءات السريعة — القائمة تُبنى حسب صلاحيات المستخدم (البند 16). */
export function QuickActions() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { open, toggle, close, containerRef } = useDropdown();

  // كل إجراء يظهر فقط إن كان مساره يُفتح للمستخدم (نفس شرط حماية المسار)
  const { permissions, isSuperAdmin: isAdmin } = useAccess();

  const actions = useMemo(() => {
    const list = [];

    // إنشاء أمر عمل أوّلاً: هو أكثر ما يُفتح، وقالبه كان بلا مدخل في الواجهة
    // فلا يصله أحد إلا بكتابة المسار.
    if (canOpen("/work-orders/new/construction")) {
      list.push({
        key: "new-construction",
        icon: faFileCirclePlus,
        label: t("quickActions.newConstruction"),
        path: "/work-orders/new/construction",
      });
    }

    // بقية الأنواع: كل نوع يفتح صفحة إنشائه المستقلة (نفس مكان دخول الإنشاءات)
    [
      ["new-maintenance", "Maintenance.Create", "quickActions.newMaintenance", "/work-orders/new/maintenance"],
      ["new-emergency", "Emergency.Create", "quickActions.newEmergency", "/work-orders/new/emergency"],
      ["new-rehabilitation", "NewProject.Create", "quickActions.newRehabilitation", "/work-orders/new/rehabilitation"],
      ["new-private", "PrivateProject.Create", "quickActions.newPrivate", "/work-orders/new/private"],
    ].forEach(([key, perm, label, path]) => {
      if (canOpen(path)) list.push({ key, icon: faFileCirclePlus, label: t(label), path });
    });

    if (canOpen("/monitoring")) {
      list.push({
        key: "monitoring",
        icon: faGaugeHigh,
        label: t("quickActions.monitoring"),
        path: "/monitoring",
      });
    }

    if (canOpen("/pdc-indicators")) {
      list.push({ key: "pdc", icon: faChartColumn, label: t("quickActions.pdc"), path: "/pdc-indicators" });
    }

    if (canOpen("/daily-execution")) {
      list.push({
        key: "daily-execution",
        icon: faPlus,
        label: t("quickActions.dailyExecution"),
        path: "/daily-execution",
      });
    }
    if (canOpen("/search-requests")) {
      list.push({
        key: "search",
        icon: faMagnifyingGlass,
        label: t("quickActions.searchWorkOrder"),
        path: "/search-requests",
      });
    }
    if (canOpen("/contract-settings")) {
      list.push({ key: "settings", icon: faGear, label: t("quickActions.settings"), path: "/contract-settings" });
    }

    return list;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [t, permissions, isAdmin]);

  if (actions.length === 0) return null;

  return (
    <div className="ah-quick" ref={containerRef}>
      <button
        type="button"
        className="ah-quick__trigger"
        onClick={toggle}
        aria-expanded={open}
        aria-haspopup="menu"
        aria-label={t("header.newAction")}
        data-testid="gh-new"
      >
        <FontAwesomeIcon icon={faPlus} />
        <span className="ah-quick__label">{t("header.newAction")}</span>
        <FontAwesomeIcon icon={faChevronDown} className={`ah-caret ${open ? "ah-caret--open" : ""}`} />
      </button>

      {open && (
        <div className="ah-menu ah-menu--quick" role="menu">
          <div className="ah-menu__head">
            <strong>{t("quickActions.title")}</strong>
          </div>
          {actions.map((action) => (
            <button
              key={action.key}
              type="button"
              className="ah-menu__item"
              role="menuitem"
              onClick={() => {
                close();
                navigate(action.path);
              }}
            >
              <FontAwesomeIcon icon={action.icon} />
              {action.label}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
